// i18next bootstrap for iPoultry (English / Bahasa Melayu).
// See I18N_PLAN.md for the phased rollout and namespace map.
//
// Resources are bundled at build time (not lazily fetched) so i18next
// initialises synchronously and the first paint is never untranslated.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { applyLocale } from './locale';

import enCommon from './en/common.json';
import msCommon from './ms/common.json';
import enAuth from './en/auth.json';
import msAuth from './ms/auth.json';
import enDashboard from './en/dashboard.json';
import msDashboard from './ms/dashboard.json';
import enBatch from './en/batch.json';
import msBatch from './ms/batch.json';
import enUser from './en/user.json';
import msUser from './ms/user.json';
import enFarm from './en/farm.json';
import msFarm from './ms/farm.json';
import enAnalytics from './en/analytics.json';
import msAnalytics from './ms/analytics.json';
import enDevice from './en/device.json';
import msDevice from './ms/device.json';
import enCharts from './en/charts.json';
import msCharts from './ms/charts.json';
import enChat from './en/chat.json';
import msChat from './ms/chat.json';
import enForms from './en/forms.json';
import msForms from './ms/forms.json';

export const DEFAULT_LANGUAGE = 'en';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ms', label: 'Bahasa Melayu', short: 'BM' },
];

// localStorage key. Deliberately NOT cleared by removeUserSession() in
// utils/common.js, so the choice survives logout and is present on the login
// screen for the next user of the browser.
export const LANGUAGE_STORAGE_KEY = 'lang';

// One namespace per feature cluster.
//
// NOTE on 'chat': the ChickenChatWidget consumes this namespace as a plain
// object (t.someKey) rather than through t('key'), and does its own
// single-brace interpolation via .replace("{cat}", …). Keep single braces in
// chat.json — i18next's own {{…}} interpolation is not used there.
const resources = {
  en: { common: enCommon, auth: enAuth, dashboard: enDashboard, batch: enBatch, user: enUser, farm: enFarm, analytics: enAnalytics, device: enDevice, charts: enCharts, chat: enChat, forms: enForms },
  ms: { common: msCommon, auth: msAuth, dashboard: msDashboard, batch: msBatch, user: msUser, farm: msFarm, analytics: msAnalytics, device: msDevice, charts: msCharts, chat: msChat, forms: msForms },
};

export const NAMESPACES = Object.keys(resources.en);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: NAMESPACES,
    defaultNS: 'common',
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // 'ms-MY' / 'en-GB' resolve to 'ms' / 'en'
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
      // Persist the bare code ('en', not 'en-US'). Without this the detector
      // caches the raw navigator value, which would later be sent verbatim to
      // the users.language column in Phase 4. Must be a function in
      // i18next-browser-languagedetector v8 — the 'strip-region' string
      // shorthand is not supported there.
      convertDetectedLanguage: (lng) => String(lng || '').split('-')[0].toLowerCase(),
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnEmptyString: false, // empty string in a JSON file falls back to English
  });

// Keep moment / react-datepicker / <html lang> in step with i18next.
applyLocale(i18n.language);
i18n.on('languageChanged', applyLocale);

export const changeLanguage = (code) => i18n.changeLanguage(code);

/**
 * Apply a language that came from the server (users.language) — Phase 4.
 *
 * The server value wins over localStorage on login, so the preference follows
 * the user across devices. A null/blank/unsupported value means "no server
 * preference" (e.g. the migration has not been applied, or this is a pre-Phase-4
 * row), in which case whatever the browser already had is kept.
 *
 * Returns true if a server preference was applied.
 */
export const applyServerLanguage = (language) => {
  const code = String(language || '').toLowerCase().split('-')[0];
  if (!code) return false;
  if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return false;
  if (i18n.resolvedLanguage !== code) i18n.changeLanguage(code);
  return true;
};

export default i18n;
